import { Injectable, inject, signal } from '@angular/core';
import {
  COGNITO_AUTH_CLIENT,
  type ResetPasswordOutcome,
  type SignInOutcome,
} from './cognito-auth-client';
import { PlatformContextService } from '../platform/platform-context.service';

export type AuthenticationState = 'checking' | 'authenticated' | 'unauthenticated';

export interface AuthSessionSnapshot {
  readonly state: AuthenticationState;
  readonly accessToken: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly client = inject(COGNITO_AUTH_CLIENT);
  private readonly platformContext = inject(PlatformContextService);
  private readonly authenticationState = signal<AuthenticationState>('checking');
  private restoration: Promise<boolean> | null = null;
  private invalidation: Promise<void> | null = null;

  readonly state = this.authenticationState.asReadonly();

  async signIn(username: string, password: string): Promise<SignInOutcome> {
    try {
      return await this.applySignInOutcome(await this.client.signIn(username, password));
    } catch (error) {
      this.authenticationState.set('unauthenticated');
      throw error;
    }
  }

  async confirmSignIn(challengeResponse: string): Promise<SignInOutcome> {
    try {
      return await this.applySignInOutcome(await this.client.confirmSignIn(challengeResponse));
    } catch (error) {
      this.authenticationState.set('unauthenticated');
      throw error;
    }
  }

  startPasswordReset(username: string): Promise<ResetPasswordOutcome> {
    return this.client.startPasswordReset(username);
  }

  confirmPasswordReset(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void> {
    return this.client.confirmPasswordReset(username, confirmationCode, newPassword);
  }

  changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return this.client.updatePassword(oldPassword, newPassword);
  }
  async signOut(): Promise<void> {
    try {
      await this.client.signOut();
    } finally {
      this.platformContext.clear();
      this.authenticationState.set('unauthenticated');
    }
  }

  restoreSession(): Promise<boolean> {
    if (this.authenticationState() !== 'checking') {
      return Promise.resolve(this.authenticationState() === 'authenticated');
    }

    this.restoration ??= this.resolveInitialSession();
    return this.restoration;
  }

  shouldHandleUnauthorizedResponse(): boolean {
    return this.authenticationState() !== 'unauthenticated' && this.invalidation === null;
  }
  isSessionInvalidationInProgress(): boolean {
    return this.invalidation !== null;
  }
  async invalidateSession(): Promise<void> {
    this.platformContext.clear();
    this.authenticationState.set('unauthenticated');
    this.invalidation ??= this.client
      .signOut()
      .catch(() => undefined)
      .finally(() => {
        this.invalidation = null;
      });
    await this.invalidation;
  }

  async getSession(): Promise<AuthSessionSnapshot> {
    const accessToken = await this.getAccessToken();
    return { state: this.authenticationState(), accessToken };
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const accessToken = await this.client.getAccessToken();
      this.authenticationState.set(accessToken === null ? 'unauthenticated' : 'authenticated');
      return accessToken;
    } catch (error) {
      this.authenticationState.set('unauthenticated');
      throw error;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    return this.authenticationState() === 'checking'
      ? this.restoreSession()
      : this.authenticationState() === 'authenticated';
  }

  private async resolveInitialSession(): Promise<boolean> {
    try {
      return (await this.getAccessToken()) !== null;
    } catch {
      this.authenticationState.set('unauthenticated');
      return false;
    }
  }

  private async applySignInOutcome(result: SignInOutcome): Promise<SignInOutcome> {
    if (result.isSignedIn) {
      await this.getAccessToken();
    } else {
      this.authenticationState.set('unauthenticated');
    }
    return result;
  }
}
